import * as s from "../VideoShareStyled";
import { Option } from "utils/options";
import { Text } from "components/atoms/text/Text";
import USER_ICON from "assets/icons/user-comment.svg";
import { ReactComponent as TRASH_ICON } from "assets/icons/trash.svg";
import Input from "components/atoms/input/Input";
import { changeStatusString } from "utils/functions";
import {
  NewValueInterface,
  VideoShareCommentListResult,
  VideoShareViewResult,
} from "api/interfaces/videoshareInterface.interface";

import { Fragment, useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createVideoShareComment,
  deleteVideoShareComment,
  getVideoShareViewCommentByVideoId,
  updateVideoShareContent,
} from "api/videoshareAPI";
import { notify } from "components/atoms/notification/Notification";
import Hashtag from "components/blocks/hashtag/Hashtag";
import TextArea from "components/atoms/input/TextArea";
import { useAuth } from "components/Auth";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  checkTimezone,
  convertVideoStartMs,
  getAccountTimeFormat,
} from "utils/timeUtil";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import RVSMWrapper from "./RVSMWrapper";
import Toggle from "components/atoms/toggle/Toggle";
import TableButton from "components/atoms/buttons/TableButton";
import { TbInfoTriangleFilled } from "react-icons/tb";

type Props = {
  videoId: string;
  state: VideoShareViewResult;
};

export default function ViewVSContent({ videoId, state }: Props): JSX.Element {
  // console.log(state);
  const { user } = useAuth();
  const { handleErrorNotify, intlString, lang, notifySuccess } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryClient = useQueryClient();

  const tabMenu = [
    {
      label: "Information",
    },
    {
      label: "Comments",
    },
  ];

  const [isSelected, setIsSelected] = useState<number>(0);
  const [isEditDetail, setIsEditDetail] = useState({
    title: false,
    tags: false,
  });

  const [savedValue, setSavedValue] = useState<NewValueInterface>({
    title: state.title,
    tags: state.tags ? state.tags.map((tag) => tag.tag) : [],
    status: state.status,
    sharing: state.sharing,
  });

  const [newValue, setNewValue] = useState<NewValueInterface>({
    title: state.title,
    tags: state.tags ? state.tags.map((tag) => tag.tag) : [],
    status: state.status,
    sharing: state.sharing,
  });

  const hashTagKeyDownHandler = useCallback(
    (event: CustomEvent<Tagify.KeydownEventData<Tagify.TagData>>) => {
      if (event.detail.event.key === "Escape") {
        setNewValue({
          ...newValue,
          tags: savedValue.tags,
        });
        setIsEditDetail((editD) => {
          return {
            ...editD,
            tags: false,
          };
        });
      }
    },
    [savedValue]
  );

  const onChangeViewValue = useCallback((e: any) => {
    let {
      target: { value, name },
    } = e;

    onChangeNewValue(name, value);
  }, []);

  const onChangeNewValue = useCallback((name: string, value: any) => {
    setNewValue((vValue) => {
      return {
        ...vValue,
        [name]: value,
      };
    });
  }, []);

  const mutationUpdateVS = useMutation(updateVideoShareContent, {
    onSuccess: (data: any, variables: any, context: any) => {
      setSavedValue((vValue) => {
        return {
          ...vValue,
          [variables.key]: variables.value,
        };
      });
      // if(variables.key === "title"){
      //   onUpdateContentValues(variables.key, variables.value);
      // }

      notifySuccess(lang.label.videoshare.notify.save.success);
      queryClient.invalidateQueries("videoshareView");
      //RND-527 Refresh tag list
      if (variables.key === "tags") {
        queryClient.invalidateQueries("videoShareHashTag");
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.save.fail);
    },
  });

  const onUpdate = useCallback(
    (key: string) => {
      // if (key === "title" || key === "status") {
      //   console.log(newValue[key] === state[key]);
      //   if (newValue[key] === state[key]) return;
      // }

      mutationUpdateVS.mutate({
        videoId: videoId,
        key: key,
        value: newValue[key],
      });
    },
    [mutationUpdateVS, newValue, videoId]
  );

  const onChangeSharing = useCallback(
    (e: boolean) => {
      onChangeNewValue("sharing", e);
      mutationUpdateVS.mutate({
        videoId: videoId,
        key: "sharing",
        value: e,
      });
    },
    [mutationUpdateVS, onChangeNewValue, videoId]
  );

  const onSaveNewValue = (key: string) => {
    setIsEditDetail((editD) => {
      return {
        ...editD,
        [key]: false,
      };
    });
    onUpdate(key);
  };

  const onSaveUndo = useCallback(
    (key: string) => {
      setNewValue({
        ...newValue,
        [key]: savedValue[key],
      });

      setIsEditDetail((editD) => {
        return {
          ...editD,
          [key]: false,
        };
      });
    },
    [newValue, savedValue]
  );

  const onChangeSelected = useCallback((index: number) => {
    setIsSelected(index);
  }, []);

  useEffect(() => {
    setNewValue({
      title: state.title,
      tags: state.tags ? state.tags.map((tag) => tag.tag) : [],
      status: state.status,
      sharing: state.sharing,
    });

    setSavedValue({
      title: state.title,
      tags: state.tags ? state.tags.map((tag) => tag.tag) : [],
      status: state.status,
      sharing: state.sharing,
    });
  }, [state]);

  const [commentList, setCommentList] = useState<
    VideoShareCommentListResult[] | undefined
  >();

  const [commentValue, setCommentValue] = useState({
    comment: "",
    warning: false,
  });

  const onChangeCommentValue = useCallback((e: any) => {
    let {
      target: { checked, value, name },
    } = e;

    if (name === "comment" && value.length >= 250) return;
    setCommentValue((comValue) => {
      if (name === "warning") {
        return {
          ...comValue,
          [name]: Boolean(checked),
        };
      }
      return {
        ...comValue,
        [name]: value,
      };
    });
  }, []);

  const mutationCreateComment = useMutation(createVideoShareComment, {
    onSuccess: () => {
      notifySuccess(lang.label.videoshare.notify.commentAdd.success);
      commentListQuery.refetch();
      setCommentValue({
        comment: "",
        warning: false,
      });
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.commentAdd.fail);
    },
  });

  const mutationDeleteComment = useMutation(deleteVideoShareComment, {
    onSuccess: () => {
      notifySuccess(lang.label.alert.notify.commentDel.success);
      commentListQuery.refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.commentDel.fail);
    },
  });

  const onSendComment = useCallback(() => {
    if (user) {
      if (commentValue.comment.length >= 250) {
        notify("warning", intlString(lang.label.videoshare.notify.maxComment));
        return;
      }
      if (commentValue.comment !== "") {
        mutationCreateComment.mutate({
          videoId: videoId,
          payload: { ...commentValue, username: user.name, email: user.email },
        });
      }
    }
  }, [commentValue, mutationCreateComment, user, videoId]);

  const onDeleteComment = useCallback(
    (item: VideoShareCommentListResult) => {
      mutationDeleteComment.mutate({
        commentId: item.id,
      });
    },
    [user]
  );

  const commentListQuery = useQuery(
    ["videoshareViewCommentList"],
    () =>
      getVideoShareViewCommentByVideoId({
        videoId: videoId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        // console.log(videoId);
        // console.log(res, "video share view comment");
        setCommentList(res.result);
      },
      onError: (e: any) => {
        console.log(e, "error");
      },
    }
  );

  const getTimezone = () => {
    const serverTimezone = checkTimezone(
      state.displayTimeZone ?? state.timeZone
    );
    if (serverTimezone === "") {
      return selectedAccount.timezone;
    }
    return serverTimezone;
  };

  return (
    <s.RightAside>
      <s.Tab>
        {tabMenu.map((menu, index) => (
          <s.TabItem
            key={`tab-menu-${index}`}
            isSelected={isSelected === index}
            onClick={() => onChangeSelected(index)}
            data-testid="videoshare-view-view-tab-item-change-selected"
          >
            {menu.label}
          </s.TabItem>
        ))}
      </s.Tab>
      {isSelected === 0 ? (
        <s.RightWrapper>
          <s.Contents isSelected={isSelected}>
            <div className="video-share-content">
              {state.uploadStatus === "success" && (
                <RVSMWrapper>
                  <Toggle
                    isToggled={state.sharing ?? true}
                    checked={state.sharing ?? true}
                    // label={"Subscribed"}
                    label="Sharing"
                    onChange={(checked: boolean) => {
                      onChangeSharing(checked);
                    }}
                    dataTestId="videoshare-view-view-sharing-toggle"
                  />
                </RVSMWrapper>
              )}

              <s.Top>
                <s.ButtonWrapper>
                  {isEditDetail.title ? (
                    <>
                      <Input
                        name="title"
                        maxLength={255}
                        value={newValue.title}
                        onChange={onChangeViewValue}
                        data-testid="videoshare-view-view-title-change-view-value"
                      />

                      <RVSMWrapper>
                        <s.TextButton
                          className="cancel"
                          onClick={() => onSaveUndo("title")}
                          data-testid="videoshare-view-view-text-button-save-undo"
                        >
                          CANCEL
                        </s.TextButton>
                        <s.TextButton
                          className="edit"
                          onClick={() => onSaveNewValue("title")}
                          data-testid="videoshare-view-view-text-button-save-new-value"
                        >
                          SAVE
                        </s.TextButton>
                      </RVSMWrapper>
                    </>
                  ) : (
                    <>
                      <s.TitleContainer>
                        <s.Title>
                          {newValue.title ? newValue.title : "Undefined"}
                        </s.Title>
                      </s.TitleContainer>
                      <RVSMWrapper>
                        <s.TextButton
                          className="edit"
                          onClick={() => {
                            setIsEditDetail((editD) => {
                              return {
                                ...editD,
                                title: true,
                              };
                            });
                          }}
                          data-testid="videoshare-view-view-text-button-set-is-edit-detail"
                        >
                          EDIT
                        </s.TextButton>
                      </RVSMWrapper>
                    </>
                  )}
                </s.ButtonWrapper>
              </s.Top>

              <div className="part top-contents">
                {state.recorderName && (
                  <s.ContentsItem>
                    <s.SubTitle>Recorder</s.SubTitle>
                    <Text>{state.recorderName}</Text>
                  </s.ContentsItem>
                )}

                {state.cameraName && (
                  <s.ContentsItem>
                    <s.SubTitle>Camera</s.SubTitle>
                    <Text>{state.cameraName}</Text>
                  </s.ContentsItem>
                )}

                {state.startTimeMs > 0 && (
                  <s.ContentsItem>
                    <s.SubTitle>Video range (From - To)</s.SubTitle>
                    <Text>
                      {convertVideoStartMs(
                        String(state.startTimeMs ?? 0),
                        getTimezone(),
                        selectedAccount
                      ) +
                        " - " +
                        convertVideoStartMs(
                          String(state.endTimeMs ?? 0),
                          getTimezone(),
                          selectedAccount
                        )}
                    </Text>
                    {/* <Text>{changeTimeStampFormat(state.timestamp)}</Text> */}
                  </s.ContentsItem>
                )}

                {state.description !== undefined && state.description !== "" && (
                  <s.ContentsItem>
                    <s.SubTitle>Description</s.SubTitle>
                    <s.Description title={state.description}>
                      {state.description}
                    </s.Description>
                  </s.ContentsItem>
                )}
              </div>

              <div className="bottom-contents">
                <s.ContentsRow className="border-top part">
                  <li>
                    <s.SubTitle>Uploaded Video Size</s.SubTitle>
                    <Text>{state.size} MB</Text>
                  </li>
                  <li>
                    <s.SubTitle>Uploaded Date and Time</s.SubTitle>
                    <Text>
                      {getAccountTimeFormat(state.date, selectedAccount, true)}
                    </Text>
                  </li>
                  {/* <li>
                  <s.SubTitle>Created by</s.SubTitle>
                  <Text>{state.username}</Text>
                  </li> */}
                </s.ContentsRow>
                <s.ContentsRow className="part">
                  <li>
                    <s.SubTitle>Created By</s.SubTitle>
                    <Text>{state.username}</Text>
                  </li>
                </s.ContentsRow>
              </div>
            </div>
          </s.Contents>
        </s.RightWrapper>
      ) : (
        <s.RightWrapper>
          <s.CommentContainer>
            <div className="top">
              <s.Title>Comments</s.Title>
              <s.CommentWrapper>
                {commentList?.map((item, index) => (
                  <s.CommentItem key={index} isWarning={item.warning}>
                    <img
                      className="video-profile-image"
                      src={item.profileUrl ? item.profileUrl : USER_ICON}
                      alt="user icon"
                    />

                    <div className="contents">
                      <div className="top-info">
                        <div className="user-info">
                          <h4>
                            {item.warning && <TbInfoTriangleFilled />}
                            {item.username}
                          </h4>
                          <p>
                            {getAccountTimeFormat(
                              item.creationTime,
                              selectedAccount,
                              true
                            )}
                          </p>
                        </div>
                        {item.email === user.email && (
                          <RVSMWrapper>
                            <s.TextButton
                              className="cancel"
                              onClick={() => onDeleteComment(item)}
                              data-testid="videoshare-view-view-text-button-delete-comment"
                            >
                              DELETE
                            </s.TextButton>
                          </RVSMWrapper>
                        )}
                      </div>
                      <div className="comment">{item.comment}</div>
                    </div>
                  </s.CommentItem>
                ))}
              </s.CommentWrapper>
            </div>
            <RVSMWrapper>
              <s.InputWrapper>
                <TextArea
                  value={commentValue.comment}
                  onChange={onChangeCommentValue}
                  placeholder="Write a comment"
                  name="comment"
                  data-testid="videoshare-view-view-comment-change-comment-value"
                />

                <div className="send-bottom">
                  <label>
                    <input
                      type="checkbox"
                      name="warning"
                      onChange={onChangeCommentValue}
                      checked={commentValue.warning}
                      data-testid="videoshare-view-view-warning-change-comment-value"
                    />
                    Important
                  </label>
                  <TableButton
                    label="Save"
                    className="send"
                    onClick={onSendComment}
                    buttonType="primary"
                    data-testid="videoshare-view-view-table-button-send-comment"
                  />
                </div>
              </s.InputWrapper>
            </RVSMWrapper>
          </s.CommentContainer>
        </s.RightWrapper>
      )}
    </s.RightAside>
  );
}
